import {type MouseEvent, useCallback} from 'react'

import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useNavigateToComment, usePullRequestBasePath} from '../stores/layout-selectors'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useNavigate} from '@github-ui/use-navigate'

/**
 * Appends `new_files_changed=true` to an outdated comment's source-diff URL. Keeps
 * navigation to the original diff in the new Files changed experience rather than
 * falling back to the classic experience (matches `ActivityHeader.getOutdatedCommentUrl`).
 */
export function withNewFilesChanged(sourceUri: string): string {
  // `originalDiffPathUri` may be absolute or root-relative (e.g. `/owner/repo/pull/1/files/...`),
  // so resolve against the current origin (`new URL(relativePath)` alone throws, which would
  // crash the panel while rendering an outdated row).
  const url = new URL(sourceUri, ssrSafeLocation.origin)
  url.searchParams.set('new_files_changed', 'true')
  return url.toString()
}

type UseCommentNavigationArgs = {
  /** Database id of the comment to navigate to; a null/undefined id disables navigation. */
  commentId: number | null | undefined
  /** Whether the source thread is outdated; its diff may be absent from the current view. */
  isOutdated: boolean
  /** The thread's `originalDiffPathUri`, used to reach an outdated comment's original diff. */
  sourceUri: string | null | undefined
  /**
   * Durable per-comment permalink used only when the comment is outdated but carries no
   * `sourceUri` — the recovered-batch case, where an item added while its thread was
   * current is later flipped outdated by a head change and so never captured an
   * original-diff URI. Unlike `sourceUri` this is used verbatim (no
   * `new_files_changed`): it is a server-issued permalink that may already point at the
   * conversation tab, and forcing the new Files changed experience would land back on
   * the very view that no longer contains the old hunk.
   */
  fallbackHref?: string | null | undefined
  /** Optional callback run after an in-app navigation (e.g. to scroll the target into view). */
  onNavigated?: () => void
}

/**
 * Shared navigation for "jump to a review comment" affordances (Manage batch panel
 * rows, thread previews). Returns the anchor `href` plus a click handler that:
 *
 * - lets modifier / middle clicks fall through to the native `href` (new tab, copy link);
 * - routes an outdated comment to its original diff (`sourceUri` + `new_files_changed`),
 *   since the current `/changes` view may not contain the old hunk;
 * - falls back to `fallbackHref` for an outdated comment with no `sourceUri`, so a
 *   recovered item still reaches its comment instead of a `/changes` view that may
 *   no longer contain it;
 * - otherwise uses the registered files-changed `navigateToComment` callback when present,
 *   falling back to a plain `/changes#r<id>` navigation.
 *
 * Callers that render their own href (e.g. a bare `#r<id>` hash when already on the
 * files-changed route) can ignore the returned `href` and wire only `onClick`.
 *
 * Also returns `opensInFilesChanged` so callers can label the link honestly: it is
 * `false` only when the destination is the durable permalink, which may open the
 * Conversation tab rather than Files changed.
 */
export function useCommentNavigation({
  commentId,
  isOutdated,
  sourceUri,
  fallbackHref,
  onNavigated,
}: UseCommentNavigationArgs) {
  const navigateToComment = useNavigateToComment()
  const basePath = usePullRequestBasePath()
  const navigate = useNavigate()

  // An outdated comment leaves the current diff, so prefer its original-diff URI and
  // then the durable permalink before falling back to the current `/changes` view.
  const outdatedDiffHref = isOutdated && sourceUri ? withNewFilesChanged(sourceUri) : undefined
  const permalinkHref = isOutdated && !sourceUri ? (fallbackHref ?? undefined) : undefined
  const outdatedHref = outdatedDiffHref ?? permalinkHref

  const href = outdatedHref ?? (commentId != null ? `${basePath}/changes#r${commentId}` : undefined)

  // Every destination except the permalink is the Files changed view; the permalink is
  // server-issued and may open the Conversation tab instead. Callers that name the
  // destination in link text or an `aria-label` must not promise Files changed when
  // this is false, or assistive tech announces the wrong target.
  const opensInFilesChanged = permalinkHref === undefined

  const onClick = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      if (commentId == null) return
      // Let modifier / middle clicks open the anchor's href natively.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- handle locally unless the user is opening in a new tab/window
      if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return
      event?.preventDefault()

      if (outdatedHref) {
        window.location.href = outdatedHref
      } else if (navigateToComment) {
        navigateToComment(commentId)
        const currentUrl = window.location.pathname + window.location.search
        addUrlToHistoryStack(`${currentUrl}#r${commentId}`)
        onNavigated?.()
      } else {
        navigate(`${basePath}/changes#r${commentId}`)
        onNavigated?.()
      }
    },
    [commentId, outdatedHref, navigateToComment, navigate, basePath, onNavigated],
  )

  return {href, onClick, opensInFilesChanged}
}
